import React, { useEffect, useRef, useState } from 'react';
import { message } from 'antd';
import { TWModal, TWButton } from '../../../components/ui';
import { formatCurrency } from '../../../utils/baseUtils';
import referralAdminApi from '../../../services/referralAdminApi';

const ACTION_META = {
  approve: {
    title: 'Approve reward',
    confirmLabel: 'Approve',
    variant: 'primary',
    needsReason: false,
    successText: 'Reward approved',
  },
  reject: {
    title: 'Reject reward',
    confirmLabel: 'Reject',
    variant: 'danger',
    needsReason: true,
    successText: 'Reward rejected',
  },
  revoke: {
    title: 'Revoke credit',
    confirmLabel: 'Revoke Credit',
    variant: 'danger',
    needsReason: true,
    successText: 'Reward revoked',
  },
};

const textareaCls =
  'block w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 placeholder-slate-400 transition focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100 dark:placeholder-slate-500';

// action: 'approve' | 'reject' | 'revoke'. Reject/revoke zorunlu reason ister.
const RewardActionModal = ({ open, action, reward, onClose, onDone }) => {
  const cancelRef = useRef(null);
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const meta = ACTION_META[action] || ACTION_META.approve;

  useEffect(() => {
    if (open) {
      setReason('');
      setError('');
      setLoading(false);
    }
  }, [open, action]);

  const handleConfirm = async () => {
    if (!reward) return;
    if (meta.needsReason && !reason.trim()) {
      setError('A reason is required.');
      return;
    }
    setError('');
    setLoading(true);
    try {
      if (action === 'reject') {
        await referralAdminApi.reject(reward.id, { reason: reason.trim() });
      } else if (action === 'revoke') {
        await referralAdminApi.revokeCredit(reward.id, {
          reason: reason.trim(),
        });
      } else {
        await referralAdminApi.approve(reward.id);
      }
      message.success(meta.successText);
      onDone();
      onClose();
    } catch (err) {
      const apiMessage =
        typeof err?.response?.data === 'string'
          ? err.response.data
          : err?.response?.data?.message || 'Action failed';
      message.error(apiMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <TWModal
      open={open}
      onClose={onClose}
      title={meta.title}
      size="md"
      initialFocus={cancelRef}
      showCloseButton
      footer={
        <div className="flex justify-end gap-2">
          <TWButton
            ref={cancelRef}
            label="Cancel"
            disabled={loading}
            onClick={onClose}
          />
          <TWButton
            variant={meta.variant}
            label={loading ? 'Working…' : meta.confirmLabel}
            loading={loading}
            onClick={handleConfirm}
          />
        </div>
      }
    >
      <div className="space-y-3">
        <p className="text-sm text-slate-600 dark:text-slate-300">
          {reward
            ? `${reward.referrercompanyname || '—'} → ${
                reward.referredcompanyname || '—'
              } · ${formatCurrency(reward.rewardamount)}`
            : ''}
        </p>
        {meta.needsReason ? (
          <div>
            <label className="mb-1.5 block text-xs font-medium text-slate-500 dark:text-slate-400">
              Reason
            </label>
            <textarea
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              disabled={loading}
              placeholder="Explain why…"
              className={textareaCls}
            />
            {error ? (
              <p className="mt-1 text-xs text-rose-600 dark:text-rose-400">
                {error}
              </p>
            ) : null}
          </div>
        ) : (
          <p className="text-sm text-slate-500 dark:text-slate-400">
            This will activate the reward and issue the referral credit.
          </p>
        )}
      </div>
    </TWModal>
  );
};

export default RewardActionModal;
